import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Grid, IconButton, Toolbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HouseIcon from '@mui/icons-material/House';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import { toast } from 'react-toastify';
import _ from 'lodash';

import DefaultApi from '../../Api/DefaultApi';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  handleCopy,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';
import { creators as MTDBActions } from '../../Reducers/MTDB';
import appdata from '../../Services/appdata';
import './style.css';


function Tables({
  tables,
  onSelectTable
}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    table_name: '',
  });
  const [sortColumns, setSortColumns] = useState([]);
  
  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'table_name':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  const toggleFilter = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleCellClick = (rowData) => {
    if (selectedRow?.id !== rowData.row.id) {
      setSelectedRow(rowData.row);
      onSelectTable(rowData.row.table_name);
    }
  };

  const tablecolumns = useMemo(() => {
    return [
      {
        key: 'table_name',
        name: 'Table Name',
        sortable: true,
        filterable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.table_name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    table_name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
    ];
    // eslint-disable-next-line
  }, [tables]);

  const filteredRows = useMemo(() => {
    return tables?.filter((r) => {
      return (
        (filters.table_name
          ? r.name?.toLowerCase().includes(filters.table_name.toLowerCase())
          : true)
      );
    });
  }, [tables, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <IconButton aria-label="Search" onClick={toggleFilter}>
          <SearchIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          columns={tablecolumns}
          rows={sortedRows}
          headerRowHeight={filters.enabled ? 70 : 40}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          onCellClick={handleCellClick}
          style={{ maxHeight: window.innerHeight - 140, resize: 'both' }}
        />
      </FilterContext.Provider>
    </>
  );
}

function TableData({
  table_name
}) {
  const navigate = useNavigate();
  const user = appdata.getItem('user');

  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    table_name: '',
  });
  const [sortColumns, setSortColumns] = useState([]);
  const [tablecolumns, setTablecolumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case rows.find(r => r.key === sortColumn):
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    getTableCols(table_name);
    getTableRows(table_name);
  }, [table_name]);

  useEffect(() => {
    if (!user.is_admin) {
      navigate('/login');
    }
  }, []);

  const getTableCols = async (tableName) => {
    tableName = tableName || table_name;
    if (tableName) {
      DefaultApi.getTableCols(tableName)
        .then(res => {
          if (res.status === 200) {
            setTablecolumns(res.data.map((col) => {
              return {
                key: col,
                name: col,
                sortable: true,
                filterable: true,
                editable: true,
              };
            }));
          } else toast.error(res.message);
        })
        .catch(error => toast.error(error.message));
    }
  }

  const getTableRows = async (tableName) => {
    tableName = tableName || table_name;
    if (tableName) {
      DefaultApi.getTableRows(tableName)
        .then(res => {
          if (res.status === 200) {
            setRows(res.data);
          } else toast.error(res.message);
        })
        .catch(error => toast.error(error.message));
    }
  }

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handleAddRow = () => {
    if (table_name) {
      DefaultApi.addTableRow(table_name)
        .then(res => {
          if (res.status === 200) {
            toast.success('Row added successfully');
            getTableRows()
          } else toast.error(res.message);
        })
        .catch(error => toast.error(error.message));
    }
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    setRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    DefaultApi.updateTableData(table_name, updated_data)
      .then((res) => {
        if (res.status === 500) toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleClickDelete = () => {
    if (selectedRow) {
      onClickedDelete(name, [selectedRow.id]);
      setSelectedRow(null);
    }
  };

  const tableColumns = useMemo(() => {
    return tablecolumns.map((col) => {
      return {
        key: col.key,
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>{col.name}</b>
            {sortColumns.find(
              (column) => column.columnKey === col.key,
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === col.key,
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters[col.key]}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    [col.key]: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      };
    });
    // eslint-disable-next-line
  }, [rows, tablecolumns, sortColumns]);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return Object.keys(filters).every((key) => {
        return r[key]?.toLowerCase().includes(filters[key].toLowerCase());
      });
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);  
  
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Add" onClick={handleAddRow}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          columns={tableColumns}
          rows={sortedRows}
          headerRowHeight={filters.enabled ? 70 : 40}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          onCellClick={handleCellClick}
          onRowsChange={handleGridRowsUpdated}
          onCopy={handleCopy}
          onPaste={handlePaste}
          style={{ maxHeight: window.innerHeight - 140, resize: 'both' }}
        />
      </FilterContext.Provider>
    </>
  )
}

function ManageTempDB() {
  const tables = useSelector((state) => state.MTDB.tables); 
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedTable, setSelectedTable] = useState(null);

  useEffect(() => {
    dispatch(MTDBActions.mtdbGetTables());
  }, [dispatch]);

  const onSignout = () => {
    appdata.localClear();
    appdata.clear();
  };

  return tables ? (
    <div style={{ height: '100%' }}>
      <AppBar position="relative" color="default">
        <Toolbar>
          <IconButton onClick={() => navigate('/')}>
            <HouseIcon />
          </IconButton>
          <IconButton onClick={onSignout} className="sign-out-but">
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Grid container spacing={1} className="ManageTempDB">
        <Grid size={2}>
          <Tables
            tables={tables}
            onSelectTable={(table_name) =>
              setSelectedTable(table_name)
            }
          />
        </Grid>
        <Grid size={10}>
          <TableData
            table_name={selectedTable}
          />
        </Grid>
      </Grid>
    </div>
  ) : null;
}

export default ManageTempDB;
